import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { getOvertimePolicies, getOvertimePolicyById, deleteOvertimePolicy, createOvertimePolicy, updateOvertimePolicy } from '../../../services/timesheetService';

const Badge = styled.span`
  display: inline-block;
  padding: 2px 10px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  font-family: 'Nunito Sans', sans-serif;
  background: ${({ $active }) => ($active ? '#E3F7F1' : '#F2F4F7')};
  color: ${({ $active }) => ($active ? '#027A48' : '#667085')};
`;

const formatMinutes = (val) => {
  if (val == null) return '-';
  const h = Math.floor(val / 60);
  const m = val % 60;
  if (h === 0) return `${m} dk`;
  if (m === 0) return `${h} sa`;
  return `${h} sa ${m} dk`;
};

const useOvertimePolicyApi = (queryParams) => {
  const { t } = useTranslation();

  const [fetching, setFetching] = useState(true);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageSize: 10, pageIndex: 0 });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const lastValidQueryParams = useRef(null);

  useEffect(() => {
    const paramsToUse = queryParams && Object.keys(queryParams).length > 0
      ? queryParams
      : lastValidQueryParams.current || { pageNumber: 1, pageSize: 10 };
    lastValidQueryParams.current = paramsToUse;
    setFetching(true);
    getOvertimePolicies(paramsToUse)
      .then((res) => {
        const result = res.data?.data;
        const items = Array.isArray(result) ? result : (result?.data || []);
        setData(items);
        setPagination({
          totalRowCount: result?.totalRowCount || items.length,
          pageIndex: result?.pageIndex ? result.pageIndex - 1 : 0,
          pageSize: result?.pageSize || items.length || 10,
        });
      })
      .catch(() => {
        setData([]);
      })
      .finally(() => setFetching(false));
  }, [queryParams, refreshTrigger]);

  const create = useCallback(async (formData) => {
    setFetching(true);
    try {
      await createOvertimePolicy(formData);
      setRefreshTrigger((p) => p + 1);
      return true;
    } catch {
      setFetching(false);
      return false;
    }
  }, []);

  const update = useCallback(async (id, formData) => {
    setFetching(true);
    try {
      await updateOvertimePolicy(id, formData);
      setRefreshTrigger((p) => p + 1);
      return true;
    } catch {
      setFetching(false);
      return false;
    }
  }, []);

  const _delete = useCallback(async (ids) => {
    try {
      for (const id of ids) {
        await deleteOvertimePolicy(id);
      }
      setRefreshTrigger((p) => p + 1);
      return true;
    } catch {
      return false;
    }
  }, []);

  const getById = useCallback(async (id) => {
    try {
      const res = await getOvertimePolicyById(id);
      return res.data?.data || null;
    } catch {
      return null;
    }
  }, []);

  const formatTime = useCallback((v) => {
    if (!v) return '-';
    if (typeof v === 'string' && v.includes(':')) {
      const parts = v.split(':');
      return `${parts[0]}:${parts[1]}`;
    }
    return v;
  }, []);

  const columns = useMemo(() => [
    { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
    { name: 'name', label: t('overtimePolicy.name'), options: { filter: true, sort: true, filterType: 'textField', filterOptions: { defaultOp: 'like' } } },
    {
      name: 'isDefault', label: t('overtimePolicy.isDefault'),
      options: {
        filter: false, sort: false,
        customBodyRender: (v) => (
          <Badge $active={v}>{v ? t('common.yes') : t('common.no')}</Badge>
        ),
      },
    },
    {
      name: 'weeklyThresholdMinutes', label: t('overtimePolicy.weeklyThreshold'),
      options: {
        filter: false, sort: true,
        customBodyRender: (v) => formatMinutes(v),
      },
    },
    {
      name: 'dailyThresholdMinutes', label: t('overtimePolicy.dailyThreshold'),
      options: {
        filter: false, sort: true,
        customBodyRender: (v) => formatMinutes(v),
      },
    },
    {
      name: 'annualOvertimeCapMinutes', label: t('overtimePolicy.annualCap'),
      options: {
        filter: false, sort: false,
        customBodyRender: (v) => formatMinutes(v),
      },
    },
    {
      name: 'nightStartTime', label: t('overtimePolicy.nightStart'),
      options: {
        filter: false, sort: false,
        customBodyRender: (v) => formatTime(v),
      },
    },
    {
      name: 'nightEndTime', label: t('overtimePolicy.nightEnd'),
      options: {
        filter: false, sort: false,
        customBodyRender: (v) => formatTime(v),
      },
    },
    {
      name: 'nightPremiumPercentage', label: t('overtimePolicy.nightPremium'),
      options: {
        filter: false, sort: false,
        customBodyRender: (v) => v != null ? `%${v}` : '-',
      },
    },
  ], [t, formatTime]);

  return { columns, data, fetching, pagination, create, update, _delete, getById };
};

export default useOvertimePolicyApi;
